import React, { useState } from 'react';
import { useEarnings } from '../../hooks';
import type { HourlyBucket } from '../../hooks';

/*
 * EarningsPanel — the money row.
 *
 * Honesty contract (do not weaken):
 * - null renders as a dash or the line is omitted; never 0, never a placeholder.
 * - projectedDaily is an EXTRAPOLATION: rendered de-emphasized, badged
 *   "projection", captioned "not yet earned" — never styled like measured money.
 * - complete24h=false renames the 24h stat to a partial window and footnotes it.
 * - stalenessSeconds is always surfaced; past 2h the whole card carries a
 *   visible STALE warning so a big joyful number can't hide a dead pool.
 * - The chart plots e.hourly verbatim from a zero baseline. No smoothing,
 *   no invented points.
 */

// ---------- formatters ----------

const fmtDoge = (v: number | null): string | null =>
  v === null
    ? null
    : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtMxn = (v: number | null): string | null =>
  v === null ? null : `$${Math.round(v).toLocaleString('en-US')}`;

/** Duration like "8m", "1.5h", "2d" — no "ago" suffix so callers can compose. */
const fmtDuration = (s: number): string => {
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m`;
  const h = s / 3600;
  if (h < 10) return `${h.toFixed(1)}h`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.floor(h / 24)}d`;
};

// ---------- big-number cell ----------

interface StatCellProps {
  label: string;
  doge: number | null;
  mxn: number | null;
  /** Extrapolated figure — rendered visibly weaker than measured money. */
  projected?: boolean;
  /** Brand-red hero treatment (measured cells only). */
  hero?: boolean;
  caption?: string;
  className?: string;
}

const StatCell: React.FC<StatCellProps> = ({
  label,
  doge,
  mxn,
  projected = false,
  hero = false,
  caption,
  className = '',
}) => {
  const dogeStr = fmtDoge(doge);
  const mxnStr = doge === null ? null : fmtMxn(mxn);

  const numberColor = projected
    ? 'text-gray-500 dark:text-slate-400'
    : hero
    ? 'text-red-600 dark:text-red-400'
    : 'text-gray-900 dark:text-white';

  return (
    <div className={className}>
      <p className="stat-label flex items-center gap-1.5 flex-wrap">
        {label}
        {projected && (
          <span className="text-[9px] font-semibold uppercase tracking-wider px-1 py-px rounded border border-dashed border-gray-400 dark:border-slate-500 text-gray-500 dark:text-slate-400">
            projection
          </span>
        )}
      </p>

      {dogeStr === null ? (
        // Unknown is unknown: a muted dash, never a zero.
        <p
          className="mt-1.5 text-3xl font-bold text-gray-300 dark:text-slate-600 leading-none"
          aria-label={`${label}: no data yet`}
        >
          —
        </p>
      ) : (
        <p
          key={dogeStr}
          className={`animate-stat-pop mt-2 short:mt-1 font-display leading-none whitespace-nowrap text-[2rem] sm:text-[clamp(1.125rem,-0.29rem_+_2.2vw,2.375rem)] short:text-xl ${numberColor}`}
        >
          {dogeStr}
          <span className="ml-1.5 font-sans text-xs font-semibold tracking-wide text-gray-500 dark:text-slate-400">
            DOGE
          </span>
        </p>
      )}

      {/* MXN line exists only when a real price arrived — never an unconverted
          or zero-peso figure. */}
      {mxnStr !== null && (
        <p
          className={`mt-2 short:mt-1 font-display leading-none whitespace-nowrap text-lg sm:text-[clamp(0.75rem,0.35rem_+_0.7vw,1.25rem)] short:text-sm ${
            projected
              ? 'text-gray-400 dark:text-slate-500'
              : 'text-gray-700 dark:text-slate-200'
          }`}
        >
          {mxnStr}
          <span className="ml-1 font-sans text-[10px] font-semibold tracking-wide text-gray-500 dark:text-slate-400">
            MXN
          </span>
        </p>
      )}

      {caption && (
        <p className="mt-1.5 short:mt-1 text-[11px] text-gray-500 dark:text-slate-400">
          {caption}
        </p>
      )}
    </div>
  );
};

// ---------- hourly chart ----------

interface HourlyChartProps {
  buckets: HourlyBucket[]; // sorted oldest -> newest
  mxnRate: number | null;
}

const HourlyChart: React.FC<HourlyChartProps> = ({ buckets, mxnRate }) => {
  const [hover, setHover] = useState<number | null>(null);
  const n = buckets.length;
  const max = n > 0 ? Math.max(...buckets.map((b) => b.total)) : 0;
  const peak = n > 0 && max > 0 ? max : null;

  if (n === 0) {
    return (
      <div>
        <p className="stat-label mb-1">DOGE / hour — last 24h</p>
        <div className="h-24 tall:h-28 short:h-12 flex items-center justify-center border-b border-gray-200 dark:border-slate-800">
          <p className="text-xs text-gray-400 dark:text-slate-500">no hourly history yet</p>
        </div>
      </div>
    );
  }

  const hovered = hover !== null ? buckets[hover] : null;

  const moveTo = (idx: number | null) => setHover(idx);

  const onPointerMove = (ev: React.PointerEvent<HTMLDivElement>) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = ev.clientX - rect.left;
    moveTo(Math.min(n - 1, Math.max(0, Math.floor((x / rect.width) * n))));
  };

  const onKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === 'ArrowLeft') {
      ev.preventDefault();
      moveTo(hover === null ? n - 1 : Math.max(0, hover - 1));
    } else if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      moveTo(hover === null ? 0 : Math.min(n - 1, hover + 1));
    } else if (ev.key === 'Escape') {
      moveTo(null);
    }
  };

  const tooltipLeft = hover === null ? 0 : Math.min(88, Math.max(12, ((hover + 0.5) / n) * 100));

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <p className="stat-label">DOGE / hour — last 24h</p>
        {peak !== null && (
          <p className="text-[10px] tabular-nums text-gray-400 dark:text-slate-500 whitespace-nowrap">
            peak {fmtDoge(peak)}
          </p>
        )}
      </div>

      {/* Whole plot is the hit target (pointer or arrow keys) — the tooltip
          enhances; the sr-only table below carries every value without it. */}
      <div
        className="relative h-24 tall:h-28 short:h-12 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 rounded-sm"
        tabIndex={0}
        aria-label="Hourly DOGE earned over the last 24 hours. Use arrow keys to inspect each hour; values are also in the table that follows."
        onPointerMove={onPointerMove}
        onPointerDown={onPointerMove}
        onPointerLeave={() => moveTo(null)}
        onKeyDown={onKeyDown}
        onBlur={() => moveTo(null)}
      >
        <div
          className="absolute inset-0 flex items-end gap-0.5 border-b border-gray-200 dark:border-slate-800"
          aria-hidden="true"
        >
          {buckets.map((b, i) => (
            <div key={b.hoursAgo} className="flex-1 h-full flex items-end justify-center">
              <div
                className={`w-full max-w-[24px] rounded-t ${
                  hover === i
                    ? 'bg-sky-500 dark:bg-sky-400'
                    : 'bg-sky-600 dark:bg-sky-500'
                }`}
                style={{
                  height:
                    max > 0 && b.total > 0
                      ? `${Math.max(3, (b.total / max) * 100)}%`
                      : '0%',
                }}
              ></div>
            </div>
          ))}
        </div>

        {hovered && (
          <div
            className="pointer-events-none absolute -top-1 -translate-y-full -translate-x-1/2 z-10 px-2 py-1 rounded-md bg-slate-900 dark:bg-slate-800 border border-slate-700 shadow-lg whitespace-nowrap"
            style={{ left: `${tooltipLeft}%` }}
          >
            <p className="text-xs font-semibold tabular-nums text-white">
              {fmtDoge(hovered.total)} DOGE
              {mxnRate !== null && (
                <span className="text-slate-300 font-normal">
                  {' '}
                  · {fmtMxn(hovered.total * mxnRate)} MXN
                </span>
              )}
            </p>
            <p className="text-[10px] text-slate-400">
              {hovered.hoursAgo === 0 ? 'this hour' : `${hovered.hoursAgo}h ago`}
            </p>
          </div>
        )}
      </div>

      <div
        className="flex items-center justify-between mt-1 text-[10px] text-gray-400 dark:text-slate-500"
        aria-hidden="true"
      >
        <span>24h ago</span>
        <span>now</span>
      </div>

      {/* Non-hover access to every plotted value. */}
      <table className="sr-only">
        <caption>DOGE credited per hour over the last 24 hours</caption>
        <thead>
          <tr>
            <th scope="col">Hours ago</th>
            <th scope="col">DOGE credited</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b) => (
            <tr key={b.hoursAgo}>
              <td>{b.hoursAgo}</td>
              <td>{b.total.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------- panel ----------

const EarningsPanel: React.FC = () => {
  const e = useEarnings('doge');

  const hasAnyData = e.lastHour !== null || e.last24h !== null || e.hourly.length > 0;

  // Oldest (hoursAgo 23) on the left, newest (hoursAgo 0) on the right.
  const buckets = [...e.hourly].sort((a, b) => b.hoursAgo - a.hoursAgo);

  // Staleness: <30min fresh, <2h lagging, beyond that the pool has stopped
  // paying and the card must say so louder than the numbers.
  const s = e.stalenessSeconds;
  const staleTier: 'ok' | 'warn' | 'bad' | null =
    s === null ? null : s < 1800 ? 'ok' : s < 7200 ? 'warn' : 'bad';

  if (e.loading && !hasAnyData) {
    return (
      <div className="card p-4 lg:p-5 short:p-3 animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded w-40 mb-5"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
          <div className="h-16 bg-gray-200 dark:bg-slate-800 rounded"></div>
          <div className="h-16 bg-gray-200 dark:bg-slate-800 rounded"></div>
          <div className="h-16 bg-gray-200 dark:bg-slate-800 rounded"></div>
          <div className="h-16 bg-gray-200 dark:bg-slate-800 rounded md:col-span-3 xl:col-span-1"></div>
        </div>
      </div>
    );
  }

  if (e.error && !hasAnyData) {
    return (
      <div className="card p-4 lg:p-5 short:p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Dogecoin Earnings
            </h3>
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              Earnings feed unavailable — {e.error}
            </p>
          </div>
          <button
            onClick={e.refresh}
            className="px-4 py-2.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4 lg:p-5 short:p-3">
      {/* Header: identity + freshness */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-4 short:mb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 short:w-6 short:h-6 bg-sky-100 dark:bg-sky-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-sky-700 dark:text-sky-400">Ð</span>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
              Dogecoin Earnings
            </h3>
            <p className="text-[11px] text-gray-500 dark:text-slate-400 short:hidden">
              measured from pool credits
            </p>
          </div>
        </div>

        {staleTier !== null && s !== null && (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap ${
              staleTier === 'ok'
                ? 'font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10'
                : staleTier === 'warn'
                ? 'font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10'
                : 'font-semibold text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-500/10'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                staleTier === 'ok'
                  ? 'bg-emerald-500 animate-pulse'
                  : staleTier === 'warn'
                  ? 'bg-amber-500'
                  : 'bg-red-500'
              }`}
              aria-hidden="true"
            ></span>
            {staleTier === 'ok'
              ? s < 90
                ? 'LIVE · paid just now'
                : `LIVE · paid ${fmtDuration(s)} ago`
              : staleTier === 'warn'
              ? `last credit ${fmtDuration(s)} ago`
              : `STALE · no credits for ${fmtDuration(s)}`}
          </span>
        )}
      </div>

      {staleTier === 'bad' && (
        <p className="mb-4 short:mb-2 text-xs text-red-600 dark:text-red-400">
          The pool has not credited anything for {s !== null ? fmtDuration(s) : 'a while'} —
          the figures below are behind reality. Check the miners.
        </p>
      )}

      {/* The money row. The 4-up single row needs xl width for the pixel
          numerals to fit their columns; below that the chart drops to its own
          full-width row, and below md the numbers stack. */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-[1fr_1.2fr_1.1fr_1.5fr] gap-4 lg:gap-6 short:gap-3 items-start">
        <StatCell
          label="Last hour"
          doge={e.lastHour}
          mxn={e.mxn.lastHour}
          caption="measured"
        />

        <StatCell
          label={e.complete24h ? 'Last 24h' : 'Last 24h · partial'}
          doge={e.last24h}
          mxn={e.mxn.last24h}
          hero
          caption={e.complete24h ? 'measured' : 'measured — history spans less than 24h'}
        />

        <StatCell
          label="Expected / day"
          doge={e.projectedDaily}
          mxn={e.mxn.projectedDaily}
          projected
          caption="estimate from the 6h rate — not yet earned"
          className="border-t border-dashed border-gray-300 dark:border-slate-700 pt-3 md:border-t-0 md:pt-0 md:border-l md:border-dashed md:pl-4 xl:pl-6"
        />

        <div className="md:col-span-3 xl:col-span-1 border-t border-gray-100 dark:border-slate-800 pt-3 xl:border-t-0 xl:pt-0">
          <HourlyChart buckets={buckets} mxnRate={e.price.mxn} />
        </div>
      </div>

      {/* Footer: rate, price, caveats */}
      <div className="mt-4 short:mt-2 pt-3 short:pt-2 border-t border-gray-100 dark:border-slate-800 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-slate-400">
        {e.ratePerHour !== null && (
          <span className="tabular-nums font-mono">
            avg {fmtDoge(e.ratePerHour)} DOGE/h · 6h window
          </span>
        )}
        {e.price.mxn !== null && (
          <span className="tabular-nums font-mono short:hidden">
            1 DOGE = ${e.price.mxn.toFixed(2)} MXN
          </span>
        )}
        {e.price.mxn === null && (
          <span>MXN price unavailable — showing DOGE only</span>
        )}
        {!e.complete24h && (
          <span className="text-amber-600 dark:text-amber-400">
            pool history covers less than 24h
          </span>
        )}
        {e.error && hasAnyData && (
          <span className="text-amber-600 dark:text-amber-400">
            refresh failing ({e.error}) — showing last good data
          </span>
        )}
      </div>
    </div>
  );
};

export default EarningsPanel;
